import React, { useEffect } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Radio from '@mui/material/Radio';
import { StravaActivity } from '../../services/StravaService';
import { AppContext } from '../../contexts/AppContext';
import { convertDistance } from '../../utils/UnitsUtils';

interface StravaActivitySelectProps {
  activities: StravaActivity[];
  isLoading: boolean;
  selectedActivityId: number | null;
  onSelectActivity: (activityId: number) => void;
  date?: Date;
  error: string | null;
  description?: string;
}

const StravaActivitySelect: React.FC<StravaActivitySelectProps> = ({
  activities,
  isLoading,
  selectedActivityId,
  onSelectActivity,
  date,
  error,
  description
}) => {
  const intl = useIntl();
  const { userService } = React.useContext(AppContext);

  const distanceUnits = userService.user?.distanceUnits || "km";

  useEffect(() => {
    if (
      !isLoading &&
      activities.length === 1 &&
      !selectedActivityId &&
      activities[0]?.id
    ) {
      onSelectActivity(activities[0].id);
    }
  }, [activities, isLoading, selectedActivityId, onSelectActivity]);

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Box sx={{ width: '100%', mt: 1 }}>
      <Typography variant="body1" sx={{ mb: 3, textAlign: 'center' }}>
        {description || <FormattedMessage id="comparison.select.description" />}
      </Typography>

      {date && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
          <FormattedMessage 
            id="comparison.select.filteringDate" 
            values={{ date: date.toLocaleDateString() }} 
          />
        </Typography>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 6 }}>
          <CircularProgress />
        </Box>
      ) : activities.length > 0 ? (
        <List sx={{ width: '100%', pb: 4 }}>
          {activities.map((activity) => (
            <ListItem
              key={activity.id}
              disablePadding
              sx={{ mb: 2 }}
              onClick={() => onSelectActivity(activity.id)}
            >
              <Card 
                sx={{ 
                  width: '100%',
                  cursor: 'pointer',
                  borderColor: selectedActivityId === activity.id ? 'primary.main' : 'divider',
                  borderWidth: selectedActivityId === activity.id ? 2 : 1,
                  borderStyle: 'solid'
                }}
                elevation={selectedActivityId === activity.id ? 2 : 0}
              >
                <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                  <Radio
                    checked={selectedActivityId === activity.id}
                    onChange={() => onSelectActivity(activity.id)}
                    value={activity.id}
                  />
                  <Box sx={{ ml: 1 }}>
                    <Typography variant="subtitle1">{activity.name}</Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(activity.start_date)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {activity.type}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {intl.formatMessage(
                          { id: "data.valueUnit" }, 
                          { 
                            value: convertDistance(activity.distance / 1000, distanceUnits),
                            unit: intl.formatMessage({ id: distanceUnits }) 
                          }
                        )}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatDuration(activity.elapsed_time)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </ListItem>
          ))}
        </List>
      ) : (
        <Box sx={{ textAlign: 'center', my: 6, p: 4, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="body1" color="text.secondary">
            <FormattedMessage id="comparison.select.noActivities" />
          </Typography>
        </Box>
      )}

      {error && (
        <Typography variant="body2" color="error" sx={{ mt: 2, textAlign: 'center' }}>
          {intl.formatMessage({ id: error })}
        </Typography>
      )}
    </Box>
  );
};

export default StravaActivitySelect;